/*
  =========================================================
  YORUX Sections v1.2.4
  Scroll reveal + problem accordions + hero gallery
  Safe: does not touch cart, drawer, checkout or native logic
  =========================================================
*/

(() => {
  const root = document.documentElement;
  root.classList.add("yx-js");

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  const revealItems = new WeakSet();

  function initReveal(scope = document) {
    if (prefersReducedMotion) return;

    const items = scope.querySelectorAll(".yx-reveal, [data-yx-reveal]");
    if (!items.length) return;

    if (!("IntersectionObserver" in window)) {
      items.forEach((item) => item.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          currentObserver.unobserve(entry.target);
        });
      },
      {
        threshold: 0.14,
        rootMargin: "0px 0px -8% 0px",
      }
    );

    items.forEach((item, index) => {
      if (revealItems.has(item)) return;

      revealItems.add(item);

      if (!item.style.transitionDelay && item.dataset.yxDelay !== "none") {
        const delay = Number(item.dataset.yxDelay || index * 70);
        item.style.transitionDelay = `${Math.min(delay, 280)}ms`;
      }

      observer.observe(item);
    });
  }

  function setProblemCardState(card, shouldOpen) {
    const button = card.querySelector("[data-yx-problem-toggle]");
    const body = card.querySelector("[data-yx-problem-body]");

    card.classList.toggle("is-open", shouldOpen);

    if (button) {
      button.setAttribute("aria-expanded", shouldOpen ? "true" : "false");
      button.textContent = shouldOpen ? "−" : "+";
    }

    if (body) {
      if (shouldOpen) {
        body.removeAttribute("hidden");
      } else {
        body.setAttribute("hidden", "");
      }
    }
  }

  function closeOtherProblemCards(card) {
    const group = card.closest("[data-yx-accordion-group]");
    if (!group) return;

    const cards = group.querySelectorAll("[data-yx-problem-card]");

    cards.forEach((otherCard) => {
      if (otherCard === card) return;
      setProblemCardState(otherCard, false);
    });
  }

  function toggleProblemCard(card) {
    const shouldOpen = !card.classList.contains("is-open");

    closeOtherProblemCards(card);
    setProblemCardState(card, shouldOpen);
  }

  function initProblemCards(scope = document) {
    const cards = scope.querySelectorAll("[data-yx-problem-card]");

    cards.forEach((card, index) => {
      const button = card.querySelector("[data-yx-problem-toggle]");
      const body = card.querySelector("[data-yx-problem-body]");

      if (!button || !body) return;

      const buttonId =
        button.id || `yx-problem-toggle-${Date.now()}-${index}`;

      const bodyId = body.id || `yx-problem-body-${Date.now()}-${index}`;

      button.id = buttonId;
      body.id = bodyId;

      button.setAttribute("aria-controls", bodyId);
      button.setAttribute("aria-expanded", "false");

      body.id = bodyId;
      body.setAttribute("hidden", "");
      body.setAttribute("aria-labelledby", buttonId);

      card.classList.remove("is-open");
      card.setAttribute("tabindex", "0");
    });
  }

  function upgradeShopifyImageUrl(url, width = 1100) {
    if (!url) return "";

    try {
      const upgradedUrl = new URL(url, window.location.href);

      if (upgradedUrl.searchParams.has("width")) {
        upgradedUrl.searchParams.set("width", String(width));
        return upgradedUrl.toString();
      }

      return upgradedUrl.toString().replace(
        /([_-])\d{2,4}x(?=\.)/,
        `$1${width}x`
      );
    } catch (error) {
      return url.replace(/([_-])\d{2,4}x(?=\.)/, `$1${width}x`);
    }
  }

  function createMainImageThumbnail(card, mainImage, gallery) {
    if (gallery.querySelector(".yorux-product-gallery__item--main")) return;

    const originalSrc = mainImage.getAttribute("src") || "";
    const originalSrcset = mainImage.getAttribute("srcset") || "";
    const originalSizes = mainImage.getAttribute("sizes") || "";
    const originalAlt =
      mainImage.getAttribute("alt") || "Imagen principal YORUX";

    const item = document.createElement("button");
    item.type = "button";
    item.className =
      "yorux-product-gallery__item yorux-product-gallery__item--main is-active";
    item.setAttribute("aria-label", "Ver imagen principal");

    const image = document.createElement("img");
    image.className = "yorux-product-gallery__image";
    image.src = originalSrc;
    image.alt = originalAlt;
    image.loading = "lazy";

    image.dataset.yxMainSrc = originalSrc;
    image.dataset.yxMainSrcset = originalSrcset;
    image.dataset.yxMainSizes = originalSizes;

    item.appendChild(image);
    gallery.insertBefore(item, gallery.firstChild);
  }

  function initHeroGallery(scope = document) {
    const productCards = scope.querySelectorAll(".yorux-product-card");

    productCards.forEach((card) => {
      const mainImage = card.querySelector(".yorux-product-card__image");
      const gallery = card.querySelector(".yorux-product-gallery");

      if (!mainImage || !gallery) return;

      createMainImageThumbnail(card, mainImage, gallery);

      const galleryItems = gallery.querySelectorAll(
        ".yorux-product-gallery__item"
      );

      galleryItems.forEach((item) => {
        if (item.dataset.yxGalleryInitialized === "true") return;

        item.dataset.yxGalleryInitialized = "true";

        if (item.tagName.toLowerCase() !== "button") {
          item.setAttribute("role", "button");
          item.setAttribute("tabindex", "0");
        }

        const changeImage = () => {
          const thumbnail = item.querySelector(".yorux-product-gallery__image");
          if (!thumbnail) return;

          const currentActive = gallery.querySelector(
            ".yorux-product-gallery__item.is-active"
          );

          if (currentActive) currentActive.classList.remove("is-active");

          item.classList.add("is-active");

          const sourceFromData = thumbnail.dataset.yxMainSrc;
          const sourceFromImage =
            thumbnail.currentSrc || thumbnail.getAttribute("src") || "";

          const nextSrc = upgradeShopifyImageUrl(
            sourceFromData || sourceFromImage,
            1100
          );

          const nextSrcset = thumbnail.dataset.yxMainSrcset || "";
          const nextSizes = thumbnail.dataset.yxMainSizes || "";

          if (nextSrc) mainImage.setAttribute("src", nextSrc);

          if (nextSrcset) {
            mainImage.setAttribute("srcset", nextSrcset);
          } else {
            mainImage.removeAttribute("srcset");
          }

          if (nextSizes) {
            mainImage.setAttribute("sizes", nextSizes);
          } else {
            mainImage.setAttribute("sizes", "(max-width: 989px) 88vw, 38vw");
          }

          if (thumbnail.getAttribute("alt")) {
            mainImage.setAttribute("alt", thumbnail.getAttribute("alt"));
          }
        };

        item.addEventListener("click", changeImage);

        item.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          changeImage();
        });
      });
    });
  }

  function initYoruxSections(scope = document) {
    initReveal(scope);
    initProblemCards(scope);
    initHeroGallery(scope);
  }

  document.addEventListener("click", (event) => {
    const problemCard = event.target.closest("[data-yx-problem-card]");

    if (!problemCard) return;

    const isBlockedElement = event.target.closest("a, input, select, textarea");

    if (isBlockedElement) return;

    event.preventDefault();
    toggleProblemCard(problemCard);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    const problemCard = event.target.closest("[data-yx-problem-card]");

    if (!problemCard) return;

    event.preventDefault();
    toggleProblemCard(problemCard);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initYoruxSections(document);
    });
  } else {
    initYoruxSections(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initYoruxSections(event.target);
  });
})();
/* =========================================================
   YORUX - Reseñas v1.4
   Límites responsive + carga progresiva
   + Ver más / Ver menos + animaciones repetibles
   ========================================================= */

(function () {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reduceMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  function getCards(section) {
    return Array.from(
      section.querySelectorAll(
        '[data-yorux-review-card], .yorux-reviews__card'
      )
    );
  }

  function getElements(section) {
    return {
      grid: section.querySelector('.yorux-reviews__grid'),
      actions: section.querySelector('[data-yorux-reviews-actions]'),
      moreButton: section.querySelector('[data-yorux-reviews-more]'),
      lessButton: section.querySelector('[data-yorux-reviews-less]')
    };
  }

  function getInitialLimit(actions) {
    if (!actions) return 1;

    const desktopLimit = Number(
      actions.dataset.visibleLimitDesktop || 6
    );

    const mobileLimit = Number(
      actions.dataset.visibleLimitMobile || 3
    );

    const selectedLimit = mobileQuery.matches
      ? mobileLimit
      : desktopLimit;

    return Number.isFinite(selectedLimit) && selectedLimit > 0
      ? selectedLimit
      : 1;
  }

  function getBatchSize(actions) {
    if (!actions) return 5;

    const batchSize = Number(
      actions.dataset.reviewsBatchSize || 5
    );

    return Number.isFinite(batchSize) && batchSize > 0
      ? batchSize
      : 5;
  }

  function getVisibleCount(section) {
    const savedCount = Number(section.dataset.yoruxVisibleCount);

    if (Number.isFinite(savedCount) && savedCount >= 0) {
      return savedCount;
    }

    return getCards(section).filter(function (card) {
      return !card.classList.contains('is-hidden');
    }).length;
  }

  function setGridColumns(grid, visibleCount) {
    if (!grid) return;

    let columns = 1;

    if (window.innerWidth >= 990) {
      if (visibleCount === 2) {
        columns = 2;
      } else if (visibleCount === 3) {
        columns = 3;
      } else if (visibleCount === 4) {
        columns = 2;
      } else if (visibleCount >= 5) {
        columns = 3;
      }
    } else if (window.innerWidth >= 750 && visibleCount >= 2) {
      columns = 2;
    }

    grid.dataset.yoruxColumns = String(columns);
    grid.dataset.yoruxVisibleCount = String(visibleCount);
  }

  function cancelAnimations(cards) {
    cards.forEach(function (card) {
      if (typeof card.getAnimations !== 'function') return;

      card.getAnimations().forEach(function (animation) {
        animation.cancel();
      });
    });
  }

  function animateCardsIn(cards) {
    if (!cards.length) return;

    cancelAnimations(cards);

    cards.forEach(function (card, index) {
      card.classList.remove('is-hidden');
      card.classList.add('is-visible');

      if (
        reduceMotionQuery.matches ||
        typeof card.animate !== 'function'
      ) {
        return;
      }

      const animation = card.animate(
        [
          {
            opacity: 0,
            transform: 'translateY(20px)'
          },
          {
            opacity: 1,
            transform: 'translateY(0)'
          }
        ],
        {
          duration: 430,
          delay: Math.min(index * 70, 280),
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both'
        }
      );

      animation.finished
        .catch(function () {
          return null;
        })
        .finally(function () {
          animation.cancel();
        });
    });
  }

  function animateCardsOut(cards) {
    if (!cards.length) {
      return Promise.resolve();
    }

    cancelAnimations(cards);

    if (reduceMotionQuery.matches) {
      cards.forEach(function (card) {
        card.classList.add('is-hidden');
        card.classList.remove('is-visible');
      });

      return Promise.resolve();
    }

    const animations = cards
      .slice()
      .reverse()
      .map(function (card, index) {
        if (typeof card.animate !== 'function') return null;

        return card.animate(
          [
            {
              opacity: 1,
              transform: 'translateY(0)'
            },
            {
              opacity: 0,
              transform: 'translateY(14px)'
            }
          ],
          {
            duration: 220,
            delay: Math.min(index * 35, 175),
            easing: 'ease',
            fill: 'forwards'
          }
        );
      })
      .filter(Boolean);

    return Promise.all(
      animations.map(function (animation) {
        return animation.finished.catch(function () {
          return null;
        });
      })
    ).then(function () {
      cards.forEach(function (card) {
        card.classList.add('is-hidden');
        card.classList.remove('is-visible');
      });

      cancelAnimations(cards);
    });
  }

  function updateButtons(section) {
    const cards = getCards(section);
    const elements = getElements(section);

    if (!elements.actions) return;

    const initialLimit = Math.min(
      getInitialLimit(elements.actions),
      cards.length
    );

    const visibleCount = Math.min(
      getVisibleCount(section),
      cards.length
    );

    if (elements.moreButton) {
      elements.moreButton.hidden = visibleCount >= cards.length;
    }

    if (elements.lessButton) {
      elements.lessButton.hidden = visibleCount <= initialLimit;
    }

    elements.actions.hidden =
      cards.length <= initialLimit &&
      visibleCount <= initialLimit;
  }

  function applyVisibleCount(section, requestedCount) {
    const cards = getCards(section);
    const elements = getElements(section);

    const visibleCount = Math.max(
      0,
      Math.min(requestedCount, cards.length)
    );

    cards.forEach(function (card, index) {
      const shouldHide = index >= visibleCount;

      card.classList.toggle('is-hidden', shouldHide);

      if (shouldHide) {
        card.classList.remove('is-visible');
      }
    });

    section.dataset.yoruxVisibleCount = String(visibleCount);

    setGridColumns(elements.grid, visibleCount);
    updateButtons(section);
  }

  function initializeSection(section) {
    const cards = getCards(section);
    const elements = getElements(section);

    if (!cards.length) return;

    if (!elements.actions) {
      applyVisibleCount(section, cards.length);
      return;
    }

    const initialLimit = getInitialLimit(elements.actions);

    section.dataset.yoruxExpanded = 'false';

    applyVisibleCount(
      section,
      Math.min(initialLimit, cards.length)
    );
  }

  function initializeSections(scope) {
    if (
      scope.classList &&
      scope.classList.contains('yorux-reviews')
    ) {
      initializeSection(scope);
      return;
    }

    if (!scope.querySelectorAll) return;

    scope
      .querySelectorAll('.yorux-reviews')
      .forEach(function (section) {
        initializeSection(section);
      });
  }

  document.addEventListener('click', async function (event) {
    const moreButton = event.target.closest(
      '[data-yorux-reviews-more]'
    );

    const lessButton = event.target.closest(
      '[data-yorux-reviews-less]'
    );

    if (!moreButton && !lessButton) return;

    const clickedButton = moreButton || lessButton;
    const section = clickedButton.closest('.yorux-reviews');

    if (!section || section.dataset.yoruxBusy === 'true') {
      return;
    }

    const cards = getCards(section);
    const elements = getElements(section);

    if (!cards.length || !elements.actions) return;

    const initialLimit = Math.min(
      getInitialLimit(elements.actions),
      cards.length
    );

    const currentCount = getVisibleCount(section);

    section.dataset.yoruxBusy = 'true';

    if (elements.moreButton) {
      elements.moreButton.disabled = true;
    }

    if (elements.lessButton) {
      elements.lessButton.disabled = true;
    }

    if (moreButton) {
      const batchSize = getBatchSize(elements.actions);

      const nextCount = Math.min(
        currentCount + batchSize,
        cards.length
      );

      const cardsToShow = cards.slice(
        currentCount,
        nextCount
      );

      cardsToShow.forEach(function (card) {
        card.classList.remove('is-hidden');
      });

      section.dataset.yoruxVisibleCount = String(nextCount);
      section.dataset.yoruxExpanded = 'true';

      setGridColumns(elements.grid, nextCount);
      updateButtons(section);
      animateCardsIn(cardsToShow);
    }

    if (lessButton) {
      const cardsToHide = cards.slice(
        initialLimit,
        currentCount
      );

      await animateCardsOut(cardsToHide);

      section.dataset.yoruxExpanded = 'false';

      applyVisibleCount(section, initialLimit);
    }

    if (elements.moreButton) {
      elements.moreButton.disabled = false;
    }

    if (elements.lessButton) {
      elements.lessButton.disabled = false;
    }

    delete section.dataset.yoruxBusy;
  });

  let resizeTimer;

  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);

    resizeTimer = window.setTimeout(function () {
      document
        .querySelectorAll('.yorux-reviews')
        .forEach(function (section) {
          const cards = getCards(section);
          const elements = getElements(section);

          if (!cards.length || !elements.actions) return;

          const initialLimit = Math.min(
            getInitialLimit(elements.actions),
            cards.length
          );

          const isExpanded =
            section.dataset.yoruxExpanded === 'true';

          if (!isExpanded) {
            applyVisibleCount(section, initialLimit);
            return;
          }

          const currentCount = Math.max(
            getVisibleCount(section),
            initialLimit
          );

          applyVisibleCount(
            section,
            Math.min(currentCount, cards.length)
          );

          section.dataset.yoruxExpanded = 'true';
        });
    }, 180);
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initializeSections(document);
      }
    );
  } else {
    initializeSections(document);
  }

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initializeSections(event.target);
    }
  );
})();
/* YORUX - Reveal global */
(function () {
  function initYoruxReveal() {
    document.documentElement.classList.add('yorux-reveal-ready');

    const targets = document.querySelectorAll([
      '.yorux-why__content',
      '.yorux-why__comparison',
      '.yorux-cases__header',
      '.yorux-cases__card',
      '.yorux-cases__closing',
      '.yorux-reviews__header',
      '.yorux-reviews__summary',
      '.yorux-reviews__card',
      '.yorux-faq__side',
      '.yorux-faq__item'
    ].join(','));

    if (!targets.length) return;

    targets.forEach(function (target) {
      target.classList.add('yorux-reveal-item');
    });

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (target) {
        target.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px'
    });

    targets.forEach(function (target) {
      observer.observe(target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initYoruxReveal);
  } else {
    initYoruxReveal();
  }

  document.addEventListener('shopify:section:load', initYoruxReveal);
})();
/* ================================
   YORUX - Hero gallery v1.3.1
================================ */

(function () {
  function initYoruxHeroGallery(root) {
    const scope = root || document;
    const heroCards = scope.querySelectorAll('.yorux-product-card');

    heroCards.forEach(function (card) {
      if (card.dataset.yxGalleryReady === 'v131') return;

      const mainImage = card.querySelector('[data-yx-main-image]');
      const thumbs = Array.from(card.querySelectorAll('[data-yx-gallery-thumb]'));
      const prevButton = card.querySelector('[data-yx-gallery-prev]');
      const nextButton = card.querySelector('[data-yx-gallery-next]');
      const track = card.querySelector('[data-yx-gallery-track]');

      if (!mainImage || !thumbs.length) return;

      card.dataset.yxGalleryReady = 'v131';

      function centerThumb(thumb) {
        if (!track || !thumb) return;

        const targetLeft = thumb.offsetLeft - (track.clientWidth / 2) + (thumb.clientWidth / 2);

        track.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: 'smooth'
        });
      }

      function setActiveImage(index) {
        const thumb = thumbs[index];
        if (!thumb) return;

        const src = thumb.getAttribute('data-yx-main-src');
        const srcset = thumb.getAttribute('data-yx-main-srcset');
        const alt = thumb.getAttribute('data-yx-main-alt') || '';

        if (!src) return;

        card.classList.add('is-changing');

        window.setTimeout(function () {
          mainImage.src = src;

          if (srcset) {
            mainImage.srcset = srcset;
          }

          mainImage.alt = alt;

          thumbs.forEach(function (item) {
            item.classList.remove('is-active');
            item.setAttribute('aria-pressed', 'false');
          });

          thumb.classList.add('is-active');
          thumb.setAttribute('aria-pressed', 'true');

          card.classList.toggle('is-swapped', index !== 0);

          centerThumb(thumb);

          window.setTimeout(function () {
            card.classList.remove('is-changing');
          }, 120);
        }, 90);
      }

      function getActiveIndex() {
        const currentIndex = thumbs.findIndex(function (thumb) {
          return thumb.classList.contains('is-active');
        });

        return currentIndex >= 0 ? currentIndex : 0;
      }

      thumbs.forEach(function (thumb, index) {
        thumb.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');

        thumb.addEventListener('click', function (event) {
          event.preventDefault();
          setActiveImage(index);
        });
      });

      if (prevButton) {
        prevButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();

          const current = getActiveIndex();
          const next = current === 0 ? thumbs.length - 1 : current - 1;

          setActiveImage(next);
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();

          const current = getActiveIndex();
          const next = current === thumbs.length - 1 ? 0 : current + 1;

          setActiveImage(next);
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initYoruxHeroGallery(document);
    });
  } else {
    initYoruxHeroGallery(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initYoruxHeroGallery(event.target);
  });
})();